import React, { useEffect, useState } from 'react'
import usePageTitle from '../../hooks/usePageTitle';
import { Link, useNavigate, useParams } from 'react-router-dom';
import NotFound from '../system/NotFound';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux-stores/store';
import Loader from '../../components/loaders/Loader';
import { setHeader } from '../../redux-stores/slices/layoutSlice';
import { defaultAppHeaderProps } from '../../types/layout/layoutTypes';
import Avatar from 'react-avatar';
import InstagramLogo from '../../assets/icons/instagram-logo.svg'
import YoutubeLogo from '../../assets/icons/youtube-logo.svg'
import InstagramIcon from '../../assets/icons/InstagramIcon';
import { formatNumber } from '../../utils/helpers';
import InfluencerSocialContentV2 from '../../components/influencer/InfluencerSocialContentV2/InfluencerSocialContentV2';
import { INIT_IG_AUTH_ROUTE } from '../../constants/constants';
import IgConnectBenefits from '../../components/igConnect/IgConnectBenefits';
import ArrowUpIcon from '../../assets/icons/ArrowUpIcon';
import ArrowDownIcon from '../../assets/icons/ArrowDownIcon';
import CheckIcon from '../../assets/icons/CheckIcon';
import dummyProfilePicture from "../../assets/images/avatars/user-dummy-img.jpg"

const SocialAccount = () => {
    usePageTitle("Account Details")
    const { socialAccountId } = useParams();
    const socialAccounts = useSelector((state: RootState) => state.influencer.social_accounts);
    const isLoading = useSelector((state: RootState) => state.influencer.loading);
    const [showPermissions, setShowPermissions] = useState(false);
    const socialAccount = socialAccounts.find((account) => account.social_account.uuid === socialAccountId);

    const platform = socialAccount?.social_account.platform;
    const [viewIGConnection, setIGConnectionVisibility] = useState(false);
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list);
    const niches = nicheList ?? {};
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches);
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(setHeader({
            generic: false,
            page: {
                title: socialAccount ? `@${socialAccount?.social_account?.handle}` : 'Account Details',
                onBackClick: () => {
                    navigate('/i/me', { replace: true });
                }
            }
        }))

        return () => {
            dispatch(setHeader(defaultAppHeaderProps))
        }
    }, [socialAccount, dispatch])


    if (isLoading) return <Loader isLoading={isLoading} />

    if (!isLoading && !socialAccount) return <NotFound />
    return (
        <>
            <div className='app-page'>
                <div className="app-card-wrapper ">
                    <div className="app-card box-shadow-none p-075">
                        <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-3">
                                <div className="social-account-profile-avatar">
                                    {
                                        socialAccount?.social_account?.profile_picture_url ? (
                                            <img src={socialAccount?.social_account?.profile_picture_url} alt={socialAccount?.social_account?.handle} onError={(e) => (e.target as HTMLImageElement).src = dummyProfilePicture} className="social-account-profile-image lg" referrerPolicy='no-referrer' />
                                        ) : (
                                            <Avatar name={socialAccount?.social_account?.handle} size='3.75rem' className="social-account-profile-image lg" />
                                        )
                                    }
                                    <span className='social-account-platform-icon lg bottom-right' style={{ right: '0%', top: '62%' }}>
                                        {
                                            socialAccount?.social_account?.platform === 'ig' && <img src={InstagramLogo as unknown as string} alt="instagram" className='social-account-platform-icon-image' />
                                        }
                                        {
                                            socialAccount?.social_account?.platform === 'yt' && <img src={YoutubeLogo as unknown as string} alt="youtube" className='social-account-platform-icon-image' />
                                        }
                                    </span>
                                </div>
                                <div className="d-flex flex-column gap-1 w-100">
                                    <span className='fw-600 fs-16 text-dark w-100 text-truncate'>@{socialAccount?.social_account?.handle}</span>
                                    <span className='fs-14 text-muted fw-400'>{socialAccount?.social_account?.platform === 'ig' ? 'Instagram' : (socialAccount?.social_account?.platform === 'yt' ? 'YouTube' : '')}</span>
                                </div>
                            </div>
                            {
                                socialAccount?.social_account?.platform === 'ig' && socialAccount.status === 'connected' &&
                                <span className='connected-badge'>
                                    <InstagramIcon /> Connected
                                </span>
                            }
                        </div>
                        <div className="mt-4 app-sub-card p-075">
                            {
                                socialAccount?.social_account?.platform === 'ig' &&
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Posts</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.ig?.media_count ?? 0)}</span>
                                        </div>
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Engagement Score</span>
                                            <span className='social-account-stat-value'>{socialAccount?.social_account?.engagement_score}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Followers</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.ig?.followers_count ?? 0)}</span>
                                        </div>
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Following</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.ig?.follows_count ?? 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            {
                                socialAccount?.social_account?.platform === 'yt' &&
                                <div className="d-flex flex-column gap-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Videos</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.yt?.video_count ?? 0)}</span>
                                        </div>
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Engagement Score</span>
                                            <span className='social-account-stat-value'>{socialAccount?.social_account?.engagement_score}</span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Subscribers</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.yt?.subscriber_count ?? 0)}</span>
                                        </div>
                                        <div className="d-flex flex-column w-50">
                                            <span className='social-account-stat-label'>Views</span>
                                            <span className='social-account-stat-value'>{formatNumber(socialAccount?.social_account?.yt?.view_count ?? 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        {
                            socialAccount?.social_account?.platform === 'ig' && socialAccount?.status !== 'connected' &&
                            <React.Fragment>
                                <div className="content my-3">
                                    <h4 className="fs-14 fw-600 text-dark">
                                        {['verified', 'unverified'].includes(socialAccount.status) ? 'Connect' : 'Reconnect'} your Instagram to get premium collabs.
                                    </h4>
                                    <IgConnectBenefits />
                                    <Link to={INIT_IG_AUTH_ROUTE + '?social_account_id=' + socialAccount.social_account.uuid} className="btn btn-sm btn-primary rounded-sm w-100 my-2 text-uppercase fs-12">Connect with Instagram</Link>
                                </div>
                            </React.Fragment>
                        }
                    </div>
                    {
                        socialAccount?.social_account?.platform === 'ig' && Object.values(socialAccount.permissions ?? {}).length > 0 && socialAccount.status === 'connected' &&
                        <div className="d-flex flex-column gap-2 p-3 py-2">
                            <div data-bs-toggle="collapse" data-bs-target="#permissions" className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setShowPermissions(!showPermissions)}>
                                <span className='fs-12 fw-500 text-dark'>Permissions</span>
                                <span>
                                    {
                                        showPermissions ? <ArrowUpIcon /> : <ArrowDownIcon />
                                    }
                                </span>
                            </div>
                            <div className={`collapse ${showPermissions ? 'show' : 'hidden'}`} id="permissions">
                                <div className="d-flex flex-column">
                                    {
                                        Object.values(socialAccount.permissions ?? {}).map((permission, i) => <a key={i} className='d-flex align-items-center gap-2' href="#">
                                            <span><CheckIcon /></span>
                                            <span className='fs-12 fw-400 text-dark-gray'>{permission}</span>
                                        </a>)
                                    }
                                </div>
                                <Link className="btn btn-outline-primary w-100 rounded-4 my-2 d-flex gap-2 justify-content-center align-items-center font-14 fw-bold" to={INIT_IG_AUTH_ROUTE + '?force=y&' + 'social_account_id=' + socialAccount.social_account.uuid}><i className="fa-solid fa-rotate"></i>Refresh</Link>
                            </div>

                        </div>
                    }
                </div>
                {
                    socialAccount &&
                    <div className="mt-4">
                        <InfluencerSocialContentV2 socialAccount={socialAccount} />
                    </div>
                }
            </div>
        </>
    )
}

export default SocialAccount